import { Component, Output, EventEmitter, OnInit, Input } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { UnitFormattingForm } from "@gtmhub/shared/controllers/unit-formatting";
import { IUnitFormatting } from "@gtmhub/shared/models";

export interface NumericFieldFormatting {
  unit: string;
  prefix: boolean;
  suffix: boolean;
  fractionSize: number;
  isCustomUnit: boolean;
}

@Component({
  selector: "numeric-field",
  templateUrl: "./numeric-field.component.html",
  styleUrls: ["./numeric-field.component.less"],
})
export class NumericFieldComponent implements OnInit {
  public validateForm: FormGroup;
  public customUnit = "";
  public unitList = [
    { unit: "", display: "Not set" },
    { unit: "$", display: "$", prefix: false, suffix: true },
    { unit: "€", display: "€", prefix: false, suffix: true },
    { unit: "£", display: "£", prefix: false, suffix: true },
    { unit: "%", display: "%", prefix: false, suffix: true },
  ];
  public fractionSizeList = [
    { value: -1, display: "Full number", customContent: false },
    { value: 0, display: "0", customContent: true, example: "e.g. 0" },
    { value: 1, display: "1", customContent: true, example: "e.g. 0.0" },
    { value: 2, display: "2", customContent: true, example: "e.g. 0.00" },
    { value: 3, display: "3", customContent: true, example: "e.g. 0.000" },
  ];

  @Input() public incomingUnitFormat: { prefix: string; suffix: string; precision: number };
  @Output() public unitFormat = new EventEmitter();

  private initialFormatting: NumericFieldFormatting = {
    unit: this.unitList[0].unit,
    prefix: false,
    suffix: true,
    fractionSize: this.fractionSizeList[0].value,
    isCustomUnit: false,
  };
  private customFormat: IUnitFormatting = {
    prefix: "",
    suffix: "",
    fractionSize: -1,
    isCustomUnit: false,
  };

  public constructor(private formBuilder: FormBuilder, private unitFormattingForm: UnitFormattingForm) {}

  public ngOnInit(): void {
    const formatting = this.setInitialFormatting();
    this.validateForm = this.formBuilder.group(formatting);
    this.registerFormListeners();
  }

  private registerFormListeners(): void {
    this.validateForm.valueChanges.subscribe((changes) => {
      if (changes.unit === "custom") {
        this.validateForm.setValue({
          ...this.validateForm.value,
          unit: this.unitList[0].unit,
        });
        this.insertCustomFormat();
      }

      this.emitFormat();
    });
  }

  private emitFormat(): void {
    this.unitFormat.emit({
      prefix: this.validateForm.value.prefix ? this.validateForm.value.unit : "",
      suffix: this.validateForm.value.suffix ? this.validateForm.value.unit : "",
      fractionSize: this.validateForm.value.fractionSize,
      isCustomUnit: this.validateForm.value.isCustomUnit ?? false,
    });
  }

  private insertCustomFormat(): void {
    this.unitFormattingForm.open({ formatting: this.customFormat }).then(() => {
      this.validateForm.setValue({
        ...this.customFormat,
        unit: this.customFormat.prefix || this.customFormat.suffix,
        prefix: !!this.customFormat.prefix,
        suffix: !!this.customFormat.suffix,
        fractionSize: this.validateForm.value.fractionSize,
      });
      this.customUnit = this.customFormat.prefix || this.customFormat.suffix;
    });
  }

  private setInitialFormatting(): NumericFieldFormatting {
    return this.incomingUnitFormat
      ? {
          unit: this.incomingUnitFormat.prefix || this.incomingUnitFormat.suffix,
          prefix: !!this.incomingUnitFormat.prefix,
          suffix: !!this.incomingUnitFormat.suffix,
          fractionSize: this.incomingUnitFormat.precision,
          isCustomUnit: !this.unitList.some(({ unit }) => this.incomingUnitFormat.prefix === unit || this.incomingUnitFormat.suffix === unit),
        }
      : this.initialFormatting;
  }
}
